import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { PROGRAM, getAllSections, getTotalLessons, getTotalUnits } from '../data/newCurriculum';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { LogOut, User, Lock, ChevronRight, BookOpen, Clock, Trophy, Target, Bot, Mic, Play } from 'lucide-react';
import GamificationBar from '../components/GamificationBar';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProgramDashboard() {
  const { user, setUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProgress();
  }, []);

  const fetchProgress = async () => {
    try {
      const res = await axios.get(`${API}/program/progress`, { withCredentials: true });
      setProgress(res.data);
    } catch (error) {
      console.error('İlerleme yüklenemedi:', error);
      // Set default progress
      setProgress({
        basic_english: { completed: 0, total: 40 },
        cabin_crew: { completed: 0, total: 60 },
        interview_prep: { completed: 0, total: 30 },
        overall: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem('session_token');
    setUser(null);
    navigate('/');
  };

  const sections = getAllSections();

  const isSectionLocked = (section) => {
    // TEST MODE: Tüm bölümler açık
    return false;
    
    /* PRODUCTION MODE - Kilitleme mantığı:
    if (section.order === 1) return false; // Temel İngilizce her zaman açık
    
    // Kabin Memurluğu için Temel İngilizce'nin %60'ı tamamlanmalı
    if (section.order === 2) {
      const basicProgress = progress.basic_english?.completed || 0;
      const basicTotal = progress.basic_english?.total || 40;
      return (basicProgress / basicTotal) * 100 < 60;
    }
    
    // Mülakat Hazırlık için Kabin Memurluğu'nun %70'i tamamlanmalı
    if (section.order === 3) {
      const cabinProgress = progress.cabin_crew?.completed || 0;
      const cabinTotal = progress.cabin_crew?.total || 60;
      return (cabinProgress / cabinTotal) * 100 < 70;
    }
    
    return false;
    */
  };

  const getSectionProgress = (sectionId) => {
    const sectionProgress = progress[sectionId];
    if (!sectionProgress) return 0;
    return Math.round((sectionProgress.completed / sectionProgress.total) * 100);
  };

  const getUnlockRequirement = (section) => {
    if (section.order === 2) return 'Temel İngilizce\'nin %60\'ını tamamlayın';
    if (section.order === 3) return 'Kabin Memurluğu\'nun %70\'ini tamamlayın';
    return '';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="text-3xl">✈️</div>
            <div>
              <h1 className="text-xl font-bold text-gray-800">Cabin Crew Training</h1>
              <p className="text-xs text-gray-500">Kabin Memuru Yetiştirme Programı</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/profile')}
              className="text-gray-600"
            >
              <User className="h-4 w-4 mr-2" />
              {user?.name || 'Profil'}
            </Button>
            {user?.role === 'admin' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/admin')}
              >
                Admin
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Çıkış
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Gamification Stats */}
        <div className="mb-6">
          <GamificationBar />
        </div>

        {/* Welcome + Quick Actions */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 mb-1">
              Hoş geldin, {user?.first_name || user?.name?.split(' ')[0] || 'Öğrenci'}! 👋
            </h2>
            <p className="text-gray-600">
              Kabin memuru olma yolculuğunda seni destekliyoruz.
            </p>
          </div>
          <div className="flex gap-3">
            <Button 
              onClick={() => navigate('/ai-interview')}
              className="bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700"
            >
              <Bot className="h-4 w-4 mr-2" />
              AI Mülakat
            </Button>
          </div>
        </div>

        {/* Overall Progress */}
        <Card className="mb-8 bg-gradient-to-r from-blue-500 to-indigo-600 text-white border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Trophy className="h-8 w-8" />
                <div>
                  <h3 className="font-bold text-lg">Genel İlerleme</h3>
                  <p className="text-blue-100 text-sm">Tüm programdaki ilerlemeniz</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-4xl font-bold">{progress.overall || 0}%</p>
                <p className="text-blue-100 text-sm">{getTotalUnits()} ünite • {getTotalLessons()} ders</p>
              </div>
            </div>
            <Progress value={progress.overall || 0} className="h-3 bg-blue-400" />
          </CardContent>
        </Card>

        {/* Stats Grid */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <Card className="bg-white/70 backdrop-blur-sm">
            <CardContent className="p-4 text-center">
              <BookOpen className="h-8 w-8 mx-auto text-emerald-500 mb-2" />
              <p className="text-2xl font-bold text-gray-800">{getTotalUnits()}</p>
              <p className="text-sm text-gray-500">Toplam Ünite</p>
            </CardContent>
          </Card>
          <Card className="bg-white/70 backdrop-blur-sm">
            <CardContent className="p-4 text-center">
              <Target className="h-8 w-8 mx-auto text-blue-500 mb-2" />
              <p className="text-2xl font-bold text-gray-800">{getTotalLessons()}</p>
              <p className="text-sm text-gray-500">Toplam Ders</p>
            </CardContent>
          </Card>
          <Card className="bg-white/70 backdrop-blur-sm">
            <CardContent className="p-4 text-center">
              <Clock className="h-8 w-8 mx-auto text-purple-500 mb-2" />
              <p className="text-2xl font-bold text-gray-800">~40</p>
              <p className="text-sm text-gray-500">Saat İçerik</p>
            </CardContent>
          </Card>
        </div>

        {/* Program Sections */}
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Program Bölümleri</h3>
        <div className="space-y-4">
          {sections.map((section, index) => {
            const locked = isSectionLocked(section);
            const sectionProgress = getSectionProgress(section.id);
            
            return (
              <Card 
                key={section.id}
                className={`relative overflow-hidden transition-all duration-300 ${
                  locked 
                    ? 'bg-gray-100 opacity-70' 
                    : 'bg-white hover:shadow-lg cursor-pointer hover:-translate-y-1'
                }`}
                onClick={() => !locked && navigate(`/program/${section.id}`)}
              >
                <CardContent className="p-0">
                  <div className="flex items-stretch">
                    {/* Section Icon & Number */}
                    <div className={`w-24 flex flex-col items-center justify-center bg-gradient-to-br ${section.color} text-white p-4`}>
                      <span className="text-4xl mb-1">{section.icon}</span>
                      <span className="text-xs font-medium opacity-80">BÖLÜM {section.order}</span>
                    </div>
                    
                    {/* Section Content */}
                    <div className="flex-1 p-5">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="text-lg font-bold text-gray-800 mb-1">
                            {section.title}
                          </h4>
                          <p className="text-gray-500 text-sm mb-3">
                            {section.subtitle}
                          </p>
                          <div className="flex items-center gap-4 text-sm text-gray-600">
                            <span className="flex items-center gap-1">
                              <BookOpen className="h-4 w-4" />
                              {section.units.length} ünite
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="h-4 w-4" />
                              {section.units.reduce((sum, u) => sum + u.lessons.length, 0)} ders
                            </span>
                          </div>
                        </div>
                        
                        {locked ? (
                          <div className="flex flex-col items-end">
                            <Lock className="h-6 w-6 text-gray-400 mb-1" />
                            <span className="text-xs text-gray-400 text-right max-w-[150px]">
                              {getUnlockRequirement(section)}
                            </span>
                          </div>
                        ) : (
                          <ChevronRight className="h-6 w-6 text-gray-400" />
                        )}
                      </div>
                      
                      {/* Progress Bar */}
                      {!locked && (
                        <div className="mt-4">
                          <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                            <span>İlerleme</span>
                            <span>{sectionProgress}%</span>
                          </div>
                          <Progress value={sectionProgress} className="h-2" />
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Info Box */}
        <Card className="mt-8 bg-amber-50 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <span className="text-2xl">💡</span>
              <div>
                <h4 className="font-semibold text-amber-800 mb-1">Nasıl Çalışır?</h4>
                <p className="text-amber-700 text-sm">
                  Program 3 aşamalıdır. Her bölümü sırayla tamamlamanız gerekir. 
                  Bir sonraki bölümün kilidini açmak için mevcut bölümün belirli bir yüzdesini tamamlamalısınız.
                  Dersleri tamamladıkça ilerlemeniz otomatik olarak kaydedilir.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
